// RoadmapPanel.js — real RepoRoadmap document (Now / Next / Later).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "./api";
import { useAuth } from "./AuthContext";
import { canMutateRepos, VIEWER_READ_ONLY_MESSAGE } from "./orgPermissions";
import { TASK_LABEL, TASKS_LABEL } from "./taskCopy";
import PrioritizationWorkspace from "./PrioritizationWorkspace";
import SprintDesigner from "./SprintDesigner";
import RoadmapSprintGroup from "./RoadmapSprintGroup";
import RoadmapTaskList from "./RoadmapTaskList";
import { ROADMAP_SECTIONS } from "./workTypeModel";
import { deliveryStatusLabel } from "./founderRoadmapCopy";
import "./RoadmapPanel.css";

const LANES = ROADMAP_SECTIONS;

const PRIORITIES = ["low", "medium", "high", "critical"];
const DELIVERY_STATUSES = [
  "idea",
  "planned",
  "ready_for_brief",
  "brief_ready",
  "in_progress",
  "in_review",
  "accepted",
  "blocked",
  "deferred",
];

const EMPTY_FORM = {
  title: "",
  outcome: "",
  lane: "now",
  priority: "medium",
  deliveryStatus: "idea",
  owner: "",
  blocker: "",
};

function RoadmapPanel({ repo, onOpenWorkBrief }) {
  const { user } = useAuth();
  const mutateAllowed = canMutateRepos(user);
  const [roadmap, setRoadmap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [actionItemId, setActionItemId] = useState(null);
  const [linkItemId, setLinkItemId] = useState(null);
  const [availableBriefs, setAvailableBriefs] = useState([]);
  const [selectedBriefId, setSelectedBriefId] = useState("");
  const [briefsLoading, setBriefsLoading] = useState(false);
  const [workspaceMode, setWorkspaceMode] = useState(null); // priority_center | design_gate | sprint_design
  const [activeSprint, setActiveSprint] = useState(null);
  const [commitReceipt, setCommitReceipt] = useState(null);
  const [answerItem, setAnswerItem] = useState(null);
  const [answerText, setAnswerText] = useState("");

  const loadRoadmap = useCallback(async () => {
    if (!repo?._id) return;
    try {
      setLoading(true);
      setError("");
      const response = await api.getRepoRoadmap(repo._id);
      setRoadmap(response.data.roadmap || null);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load roadmap");
    } finally {
      setLoading(false);
    }
  }, [repo?._id]);

  useEffect(() => {
    loadRoadmap();
  }, [loadRoadmap]);

  const itemsByLane = useMemo(() => {
    const items = roadmap?.items || [];
    const grouped = { now: [], next: [], later: [] };
    for (const item of items) {
      const lane = grouped[item.lane] ? item.lane : "later";
      grouped[lane].push(item);
    }
    for (const lane of Object.keys(grouped)) {
      grouped[lane].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    }
    return grouped;
  }, [roadmap]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
    setError("");
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title || "",
      outcome: item.outcome || "",
      lane: item.lane || "now",
      priority: item.priority || "medium",
      deliveryStatus: item.deliveryStatus || "idea",
      owner: item.owner || "",
      blocker: item.blocker || "",
    });
    setFormOpen(true);
    setError("");
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!repo?._id || !mutateAllowed) return;
    if (!form.title.trim()) {
      setError("Title is required.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      const payload = {
        title: form.title.trim(),
        outcome: form.outcome.trim(),
        lane: form.lane,
        priority: form.priority,
        deliveryStatus: form.deliveryStatus,
        owner: form.owner.trim(),
        blocker: form.blocker.trim(),
      };
      const response = editingId
        ? await api.updateRoadmapItem(repo._id, editingId, payload)
        : await api.createRoadmapItem(repo._id, payload);
      setRoadmap(response.data.roadmap);
      closeForm();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save roadmap item");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!repo?._id || !mutateAllowed) return;
    const ok = window.confirm(
      `Delete “${item.title}”? Linked ${TASKS_LABEL} are not deleted.`
    );
    if (!ok) return;
    try {
      setActionItemId(item.id);
      setError("");
      const response = await api.deleteRoadmapItem(repo._id, item.id);
      setRoadmap(response.data.roadmap);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete roadmap item");
    } finally {
      setActionItemId(null);
    }
  };

  const openLinkPicker = async (item) => {
    if (!repo?._id || !mutateAllowed) return;
    setLinkItemId(item.id);
    setSelectedBriefId("");
    setBriefsLoading(true);
    setError("");
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("repoId", repo._id);
      queryParams.append("offset", "0");
      queryParams.append("limit", "50");
      const response = await api.get(`/feature-plans?${queryParams.toString()}`);
      const linkedIds = new Set(
        (roadmap?.items || [])
          .map((i) => i.workBriefId)
          .filter(Boolean)
          .map(String)
      );
      // Allow re-selecting this item's current brief.
      if (item.workBriefId) linkedIds.delete(String(item.workBriefId));
      const briefs = (response.data.plans || []).filter(
        (plan) => !linkedIds.has(String(plan._id))
      );
      setAvailableBriefs(briefs);
    } catch (err) {
      setError(err.response?.data?.error || `Failed to load ${TASKS_LABEL}`);
      setAvailableBriefs([]);
    } finally {
      setBriefsLoading(false);
    }
  };

  const handleLink = async () => {
    if (!repo?._id || !linkItemId || !selectedBriefId) return;
    try {
      setActionItemId(linkItemId);
      setError("");
      const response = await api.linkRoadmapItemWorkBrief(
        repo._id,
        linkItemId,
        selectedBriefId,
        { replace: true }
      );
      setRoadmap(response.data.roadmap);
      setLinkItemId(null);
      setSelectedBriefId("");
    } catch (err) {
      setError(err.response?.data?.error || `Failed to link ${TASK_LABEL}`);
    } finally {
      setActionItemId(null);
    }
  };

  const handleUnlink = async (item) => {
    if (!repo?._id || !mutateAllowed) return;
    try {
      setActionItemId(item.id);
      setError("");
      const response = await api.unlinkRoadmapItemWorkBrief(repo._id, item.id);
      setRoadmap(response.data.roadmap);
    } catch (err) {
      setError(err.response?.data?.error || `Failed to unlink ${TASK_LABEL}`);
    } finally {
      setActionItemId(null);
    }
  };

  const handleMove = async (item, direction) => {
    if (!repo?._id || !mutateAllowed) return;
    const laneItems = itemsByLane[item.lane] || [];
    const index = laneItems.findIndex((row) => row.id === item.id);
    const swap = laneItems[index + direction];
    if (!swap) return;
    try {
      setActionItemId(item.id);
      setError("");
      await api.updateRoadmapItem(repo._id, item.id, { sortOrder: swap.sortOrder || 0 });
      const response = await api.updateRoadmapItem(repo._id, swap.id, {
        sortOrder: item.sortOrder || 0,
      });
      setRoadmap(response.data.roadmap);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to reorder");
    } finally {
      setActionItemId(null);
    }
  };

  const handleChangeWorkType = async (item, workType) => {
    if (!repo?._id || !mutateAllowed) return;
    try {
      setActionItemId(item.id);
      const response = await api.updateRoadmapItem(repo._id, item.id, { workType });
      setRoadmap(response.data.roadmap);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update work type");
    } finally {
      setActionItemId(null);
    }
  };

  const handleAuthorize = async (item) => {
    if (!repo?._id || !mutateAllowed || !item.workBriefId) return;
    try {
      setActionItemId(item.id);
      setError("");
      await api.authorizeAgentWork(repo._id, {
        featurePlanId: item.workBriefId,
        roadmapItemId: item.id,
        confirmation: true,
      });
      await loadRoadmap();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to make this available to agents");
    } finally {
      setActionItemId(null);
    }
  };

  const handleRevoke = async (item) => {
    if (!repo?._id || !mutateAllowed) return;
    const workOrderId = item.agentExecution?.workOrderId;
    if (!workOrderId) {
      await loadRoadmap();
      return;
    }
    try {
      setActionItemId(item.id);
      await api.revokeAgentWork(repo._id, workOrderId);
      await loadRoadmap();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to pause agent access");
    } finally {
      setActionItemId(null);
    }
  };

  const handleStop = async (item) => {
    if (!repo?._id || !mutateAllowed || !item.agentExecution?.attemptId) return;
    try {
      setActionItemId(item.id);
      await api.requestAgentStop(repo._id, item.agentExecution.attemptId);
      await loadRoadmap();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to request stop");
    } finally {
      setActionItemId(null);
    }
  };

  const handleAnswerSubmit = async () => {
    if (!repo?._id || !answerItem?.agentExecution?.attemptId) return;
    try {
      setActionItemId(answerItem.id);
      await api.answerAgentQuestion(repo._id, answerItem.agentExecution.attemptId, {
        answer: answerText,
        materialChange: false,
      });
      setAnswerItem(null);
      setAnswerText("");
      await loadRoadmap();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to send your answer");
    } finally {
      setActionItemId(null);
    }
  };

  const handleCreateWorkBrief = async (item) => {
    if (!repo?._id || !mutateAllowed || item.workBriefId) return;
    try {
      setActionItemId(item.id);
      setError("");
      const response = await api.createWorkBriefFromRoadmapItem(repo._id, item.id, {
        acknowledgeReadinessOverride: true,
      });
      setRoadmap(response.data.roadmap);
      const briefId = response.data.workBrief?._id;
      if (briefId && typeof onOpenWorkBrief === "function") {
        onOpenWorkBrief(briefId);
      }
    } catch (err) {
      const data = err.response?.data;
      setError(data?.message || data?.error || `Failed to create ${TASK_LABEL}`);
    } finally {
      setActionItemId(null);
    }
  };

  if (!repo) {
    return <div className="roadmap-panel">No repository selected</div>;
  }

  const coverageIntegrity = repo.analysisCoverage?.coverageIntegrity;
  const showReadinessWarning = coverageIntegrity && coverageIntegrity !== "OK";
  const items = roadmap?.items || [];

  return (
    <div className="roadmap-panel" data-testid="roadmap-panel">
      <div className="roadmap-header">
        <div className="roadmap-header-row">
          <div>
            <h2>{roadmap?.title || "Roadmap"} for {repo.owner}/{repo.name}</h2>
            <p className="roadmap-subtitle">
              {roadmap?.description?.trim()
                ? roadmap.description
                : `Plan work top to bottom. Current sprint, then up next, then later. Create or link a ${TASK_LABEL} when an item is ready.`}
            </p>
          </div>
          <div className="roadmap-header-actions">
            {!workspaceMode ? (
              <>
            <button
              type="button"
              className="primary-button"
              onClick={() => {
                setWorkspaceMode("design_gate");
                setCommitReceipt(null);
              }}
              data-testid="roadmap-design-sprint"
            >
              Design my next sprint
            </button>
            <button
              type="button"
              className="secondary-button"
              onClick={() => {
                setWorkspaceMode("priority_center");
                setCommitReceipt(null);
              }}
              data-testid="roadmap-prioritize-tasks"
            >
              Prioritize {TASKS_LABEL}
            </button>
            {mutateAllowed ? (
              <button
                type="button"
                className="secondary-button"
                onClick={openCreate}
                data-testid="roadmap-add-item"
              >
                Add roadmap item
              </button>
            ) : (
              <p className="roadmap-item-link-label">{VIEWER_READ_ONLY_MESSAGE}</p>
            )}
              </>
            ) : null}
          </div>
        </div>
      </div>

      {showReadinessWarning && (
        <div
          className="roadmap-readiness-warning"
          role="status"
          data-testid="roadmap-readiness-warning"
        >
          Repository analysis is incomplete — evidence quality may be limited. See Codebase Guide
          for readiness detail.
        </div>
      )}

      {commitReceipt ? (
        <div className="roadmap-sprint-receipt" data-testid="roadmap-sprint-receipt" role="status">
          Sprint added to the Roadmap. Created {commitReceipt.createdItemIds?.length || 0}, reused{" "}
          {commitReceipt.reusedItemIds?.length || 0}, moved {commitReceipt.movedItemIds?.length || 0}.
        </div>
      ) : null}

      {workspaceMode === "priority_center" || workspaceMode === "design_gate" ? (
        <PrioritizationWorkspace
          repo={repo}
          mutateAllowed={mutateAllowed}
          entry={workspaceMode === "design_gate" ? "design" : "prioritize"}
          onCancel={() => setWorkspaceMode(null)}
          onOpenTask={onOpenWorkBrief}
          onContinue={(design) => {
            setActiveSprint(design);
            setWorkspaceMode("sprint_design");
          }}
        />
      ) : null}
      {workspaceMode === "sprint_design" && activeSprint ? (
        <SprintDesigner
          repo={repo}
          design={activeSprint}
          mutateAllowed={mutateAllowed}
          onOpenTask={onOpenWorkBrief}
          onBack={() => setWorkspaceMode("priority_center")}
          onCommitted={(payload) => {
            setCommitReceipt(payload.receipt);
            setWorkspaceMode(null);
            setActiveSprint(null);
            loadRoadmap();
          }}
        />
      ) : null}

      {!workspaceMode ? (
        <>
      {(roadmap?.sprintGroups || []).map((group) => (
        <RoadmapSprintGroup
          key={group.id}
          group={group}
          items={items}
          onOpenTask={onOpenWorkBrief}
        />
      ))}

      {answerItem ? (
        <div className="roadmap-form" data-testid="agent-answer-form">
          <h3>Answer the agent</h3>
          <p>{answerItem.agentExecution?.question}</p>
          <textarea
            value={answerText}
            onChange={(e) => setAnswerText(e.target.value)}
            rows={3}
            data-testid="agent-answer-text"
          />
          <div className="roadmap-form-actions">
            <button type="button" className="primary-button" onClick={handleAnswerSubmit}>
              Send answer
            </button>
            <button type="button" className="secondary-button" onClick={() => setAnswerItem(null)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      {formOpen && mutateAllowed && (
        <div className="roadmap-form" data-testid="roadmap-item-form">
          <h3>{editingId ? "Edit roadmap item" : "New roadmap item"}</h3>
          <div className="roadmap-form-grid">
            <label>
              Title
              <input
                data-testid="roadmap-item-title"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                maxLength={200}
              />
            </label>
            <label>
              Lane
              <select
                data-testid="roadmap-item-lane"
                value={form.lane}
                onChange={(e) => setForm((f) => ({ ...f, lane: e.target.value }))}
              >
                {LANES.map((lane) => (
                  <option key={lane.value} value={lane.value}>
                    {lane.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Priority
              <select
                data-testid="roadmap-item-priority"
                value={form.priority}
                onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
              >
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Delivery status
              <select
                data-testid="roadmap-item-status"
                value={form.deliveryStatus}
                onChange={(e) =>
                  setForm((f) => ({ ...f, deliveryStatus: e.target.value }))
                }
              >
                {DELIVERY_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {deliveryStatusLabel(s)}
                  </option>
                ))}
              </select>
            </label>
            <label className="full">
              Outcome / request
              <textarea
                data-testid="roadmap-item-outcome"
                value={form.outcome}
                onChange={(e) => setForm((f) => ({ ...f, outcome: e.target.value }))}
                rows={3}
              />
            </label>
            <label>
              Owner
              <input
                value={form.owner}
                onChange={(e) => setForm((f) => ({ ...f, owner: e.target.value }))}
              />
            </label>
            <label>
              Blocker
              <input
                value={form.blocker}
                onChange={(e) => setForm((f) => ({ ...f, blocker: e.target.value }))}
              />
            </label>
          </div>
          <div className="roadmap-form-actions">
            <button
              type="button"
              className="primary-button"
              onClick={handleSave}
              disabled={saving}
              data-testid="roadmap-item-save"
            >
              {saving ? "Saving…" : editingId ? "Save changes" : "Create item"}
            </button>
            <button type="button" className="secondary-button" onClick={closeForm}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="roadmap-empty" data-testid="roadmap-loading">
          <p>Loading roadmap…</p>
        </div>
      ) : items.length === 0 && !formOpen ? (
        <div className="roadmap-empty" data-testid="roadmap-empty">
          <p>No roadmap items yet.</p>
          <p>
            Add a roadmap item to plan upcoming work, then create a {TASK_LABEL} when it is ready to
            hand to a developer.
          </p>
        </div>
      ) : (
        <div className="roadmap-lanes" data-testid="roadmap-lanes">
          {LANES.map((lane) => (
            <section
              key={lane.value}
              className="roadmap-lane"
              data-testid={`roadmap-lane-${lane.value}`}
            >
              <h3>
                {lane.label}
                <span className="roadmap-lane-count">
                  {itemsByLane[lane.value].length}
                </span>
              </h3>
              <RoadmapTaskList
                items={itemsByLane[lane.value]}
                mutateAllowed={mutateAllowed}
                busy={Boolean(actionItemId)}
                linkItemId={linkItemId}
                briefsLoading={briefsLoading}
                availableBriefs={availableBriefs}
                selectedBriefId={selectedBriefId}
                onSelectedBriefId={setSelectedBriefId}
                onOpenTask={onOpenWorkBrief}
                onCreateTask={handleCreateWorkBrief}
                onEdit={openEdit}
                onDelete={handleDelete}
                onLink={handleLink}
                onUnlink={handleUnlink}
                onOpenLinkPicker={openLinkPicker}
                onCloseLinkPicker={() => setLinkItemId(null)}
                onMove={handleMove}
                onChangeWorkType={handleChangeWorkType}
                onAuthorize={handleAuthorize}
                onRevoke={handleRevoke}
                onAnswer={(item) => {
                  setAnswerItem(item);
                  setAnswerText("");
                }}
                onStop={handleStop}
                onReview={onOpenWorkBrief}
              />
            </section>
          ))}
        </div>
      )}
        </>
      ) : null}
    </div>
  );
}

export default RoadmapPanel;
